/**
 * SheetsConfig - Configuration for SheetsApp_v2
 * Defines grid-specific node types and spreadsheet settings
 */

import { AppConfig, SupportedClass } from '../../../services/baseApplication/types';

/**
 * Sheets-specific supported classes
 * Uses grid positioning and cell-based layout
 */
export const SHEETS_SUPPORTED_CLASSES: Record<string, SupportedClass> = {
  // Core Grid
  'sheet': {
    displayName: 'Sheet',
    description: 'Spreadsheet grid container',
    category: 'container',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { 
        rows: 1000, 
        cols: 26, // A-Z
        defaultRowHeight: 24,
        defaultColWidth: 100
      }
    }
  },

  // Cell Types
  'cell-text': {
    displayName: 'Text Cell',
    description: 'Cell containing plain text',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      value: '',
      semantic: { cellType: 'text' }
    }
  },

  'cell-number': {
    displayName: 'Number Cell',
    description: 'Cell containing numeric value',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      value: 0,
      semantic: { cellType: 'number', format: 'general' }
    }
  },

  'cell-formula': {
    displayName: 'Formula Cell',
    description: 'Cell containing formula',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      value: null,
      semantic: { 
        cellType: 'formula', 
        formula: '', 
        dependencies: []
      }
    }
  },

  'cell-date': {
    displayName: 'Date Cell',
    description: 'Cell containing date value',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      value: '',
      semantic: { cellType: 'date', format: 'MM/DD/YYYY' }
    }
  },

  'cell-boolean': {
    displayName: 'Boolean Cell',
    description: 'Cell containing true/false',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      value: false,
      semantic: { cellType: 'boolean' }
    }
  },

  'cell-error': {
    displayName: 'Error Cell',
    description: 'Cell showing error',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { cellType: 'error', errorType: 'REF', message: '' }
    }
  },

  // Formatting & Structure
  'cell-header': {
    displayName: 'Header Cell',
    description: 'Column or row header',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      style: { fontWeight: 600, background: '#f5f5f5' }
    }
  },

  'merged-cell': {
    displayName: 'Merged Cell',
    description: 'Merged cell range',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { 
        startRow: 0, 
        startCol: 0, 
        endRow: 0, 
        endCol: 0 
      }
    }
  },

  // Rich Content
  'cell-link': {
    displayName: 'Link Cell',
    description: 'Cell containing hyperlink',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { url: '', displayText: '' }
    }
  },

  'cell-image': {
    displayName: 'Image Cell',
    description: 'Cell containing image',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { src: '', alt: '' }
    }
  },

  // Data Validation
  'cell-dropdown': {
    displayName: 'Dropdown Cell',
    description: 'Cell with dropdown options',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { 
        options: [], 
        selectedIndex: -1,
        allowCustom: false 
      }
    }
  },

  'cell-checkbox': {
    displayName: 'Checkbox Cell',
    description: 'Cell with checkbox',
    category: 'cell',
    defaultAttrs: {
      position: { type: 'flow' },
      semantic: { checked: false }
    }
  }
};

/**
 * Cell value types
 */
export type CellValueType = 'text' | 'number' | 'formula' | 'date' | 'boolean' | 'error' | 'link' | 'image';

/**
 * Number formats
 */
export const NUMBER_FORMATS = {
  general: 'General',
  number: '0.00',
  currency: '$0.00',
  accounting: '_($* #,##0.00_);_($* (#,##0.00);_($* "-"??_);_(@_)',
  percentage: '0.00%',
  scientific: '0.00E+00',
  fraction: '# ?/?',
};

/**
 * Date formats
 */
export const DATE_FORMATS = {
  short: 'M/D/YYYY',
  medium: 'MMM D, YYYY',
  long: 'MMMM D, YYYY',
  time: 'h:mm:ss A',
  datetime: 'M/D/YYYY h:mm A',
  iso: 'YYYY-MM-DD',
};

/**
 * Error types
 */
export enum FormulaError {
  REF = '#REF!',     // Invalid reference
  VALUE = '#VALUE!', // Wrong type
  DIV0 = '#DIV/0!',  // Division by zero
  NAME = '#NAME?',   // Unknown name
  NA = '#N/A',       // Not available
  NUM = '#NUM!',     // Invalid number
  NULL = '#NULL!',   // Invalid intersection
}

/**
 * Sheets Application Configuration
 */
export const SHEETS_APP_CONFIG: AppConfig = {
  appId: 'sheets_v2',
  appName: 'Sheets v2',
  containerType: 'sheet',
  layoutMode: 'grid',
  supportedClasses: SHEETS_SUPPORTED_CLASSES,
  supportedPositionTypes: ['flow'], // Cells use grid coordinates, not positioning
  supportsDragDrop: false, // Cells don't drag, but ranges can
  supportsResize: false,
  supportsRotation: false,
  defaultContainer: {
    width: undefined, // Grid-based
    height: undefined, // Grid-based
    infinite: false
  },
  
  editorSelector: '.sheets-app-v2' // ✅ Required by AppConfig
};

/**
 * Helper: Get supported className list
 */
export function getSupportedClassNames(): string[] {
  return Object.keys(SHEETS_SUPPORTED_CLASSES);
}

/**
 * Helper: Check if className is supported
 */
export function isClassNameSupported(className: string): boolean {
  return className in SHEETS_SUPPORTED_CLASSES;
}

/**
 * Helper: Get class info
 */
export function getClassInfo(className: string): SupportedClass | undefined {
  return SHEETS_SUPPORTED_CLASSES[className];
}

/**
 * Helper: Get classes by category
 */
export function getClassesByCategory(category: string): string[] {
  return Object.entries(SHEETS_SUPPORTED_CLASSES)
    .filter(([_, info]) => info.category === category)
    .map(([className]) => className);
}

/**
 * Helper: Check if className is a cell type
 */
export function isCellClassName(className: string): boolean {
  return className.startsWith('cell-') || className === 'merged-cell';
}

